<template>
  <div class="transactions-topbar">
    <!-- busca de loja -->
    <div class="topbar-search">
      <b-autocomplete
        v-model="storeSearch"
        :data="filteredOptions"
        field="name"
        placeholder="Buscar loja ou CNPJ..."
        clearable
        open-on-focus
        @select="onStoreSelect"
      >
        <template slot-scope="props">
          <div v-if="props.option.type === 'all'" class="option-all">
            <span class="material-icons option-all-icon">store</span>
            <span>{{ props.option.name }}</span>
          </div>
          <div v-else-if="props.option.type === 'group'" class="option-group">
            <span class="option-group-cnpj">{{
              formatCnpj(props.option.cnpj)
            }}</span>
            <span
              v-if="props.option.companyName"
              class="option-group-company"
              >{{ props.option.companyName }}</span
            >
            <span class="option-group-count"
              >{{ props.option.storeIds.length }} loja{{
                props.option.storeIds.length !== 1 ? "s" : ""
              }}</span
            >
          </div>
          <div v-else class="option-store">
            <span>{{ props.option.name }}</span>
            <span v-if="props.option.cnpj" class="option-cnpj-hint">{{
              formatCnpj(props.option.cnpj)
            }}</span>
          </div>
        </template>
        <template slot="empty">Nenhuma loja encontrada</template>
      </b-autocomplete>
    </div>

    <!-- ações: refresh + download -->
    <div class="topbar-actions">
      <button
        class="action-btn"
        title="Atualizar"
        @click="$emit('get-transactions')"
      >
        <b-icon icon="sync-alt" size="is-small" />
      </button>
      <button
        class="action-btn"
        title="Baixar relatório"
        @click="$emit('download-transactions-report')"
      >
        <b-icon icon="download" size="is-small" />
      </button>
    </div>

    <!-- campo de data estilizado -->
    <b-datepicker
      v-model="dates"
      range
      :mobile-native="false"
      position="is-bottom-left"
      @input="$emit('update-dates', dates)"
    >
      <template #trigger>
        <div
          class="date-trigger"
          :class="{ 'date-trigger--active': dates && dates[0] }"
        >
          <span class="date-trigger-text">
            <template v-if="dates && dates[0]">
              {{ formatDate(dates[0]) }}
              <span class="date-arrow">›</span>
              {{ formatDate(dates[1]) }}
            </template>
            <template v-else> dd/mm/aa › dd/mm/aa </template>
          </span>
          <span class="date-trigger-icon">
            <b-icon icon="calendar-alt" size="is-small" />
          </span>
        </div>
      </template>
    </b-datepicker>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";

export default {
  props: ["transactionsQty", "storeId", "storeName"],

  mixins: [common],

  data() {
    return {
      dates: null,
      storeSearch: ""
    };
  },

  computed: {
    ...mapGetters(["merchants"]),

    groupOptions() {
      return this.merchants
        .filter(m => m.stores && m.stores.length > 0)
        .map(m => ({
          type: "group",
          name: this.formatCnpj(
            (m.companyInfo && m.companyInfo.nationalCompanyId) || ""
          ),
          cnpj: (m.companyInfo && m.companyInfo.nationalCompanyId) || "",
          companyName: (m.companyInfo && m.companyInfo.companyName) || "",
          storeIds: m.stores.map(s => s._id)
        }));
    },

    storeOptions() {
      return this.merchants
        .flatMap(m =>
          (m.stores || []).map(s => ({
            type: "store",
            name: s.storeInfo.name,
            cnpj: (m.companyInfo && m.companyInfo.nationalCompanyId) || "",
            storeId: s._id
          }))
        )
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    allOption() {
      return { type: "all", name: "Todas as transações" };
    },

    filteredOptions() {
      const q = this.storeSearch.toLowerCase().trim();
      if (!q)
        return [this.allOption, ...this.groupOptions, ...this.storeOptions];

      if ("todas as transações".includes(q)) {
        return [this.allOption, ...this.groupOptions, ...this.storeOptions];
      }

      const matchingGroups = this.groupOptions.filter(g => {
        const label = `${this.formatCnpj(g.cnpj)} ${
          g.companyName
        }`.toLowerCase();
        return label.includes(q);
      });

      const matchingStores = this.storeOptions.filter(s =>
        s.name.toLowerCase().includes(q)
      );

      return [...matchingGroups, ...matchingStores];
    }
  },

  methods: {
    formatCnpj(cnpj) {
      if (!cnpj) return "";
      const c = cnpj.replace(/\D/g, "");
      if (c.length !== 14) return cnpj;
      return c.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5"
      );
    },

    formatDate(d) {
      if (!d) return "";
      const date = new Date(d);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    },

    onStoreSelect(option) {
      if (!option || option.type === "all") {
        this.storeSearch = option ? option.name : "";
        this.$emit("update-store", null);
        return;
      }
      if (option.type === "group") {
        this.$emit("update-store", {
          type: "group",
          storeIds: option.storeIds,
          label: `${this.formatCnpj(option.cnpj)}${
            option.companyName ? " — " + option.companyName : ""
          }`
        });
      } else {
        this.$emit("update-store", {
          type: "store",
          storeId: option.storeId,
          storeName: option.name
        });
      }
    }
  },

  mounted() {
    this.storeSearch = this.storeName || "Todas as transações";
    this.dates = [new Date(), new Date()];
  }
};
</script>

<style scoped>
/* ── barra principal ── */
.transactions-topbar {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

/* ── busca de loja (cresce) ── */
.topbar-search {
  flex: 1 1 220px;
  min-width: 0;
}

.topbar-search ::v-deep .input {
  border-radius: 8px;
  border-color: #e0e0e0;
  font-size: 0.875rem;
  height: 38px;
}

/* ── botões de ação ── */
.topbar-actions {
  display: flex;
  gap: 0.35rem;
  flex-shrink: 0;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  color: #555;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  outline: none;
}

.action-btn:hover {
  background: #fff4ee;
  border-color: #ff7034;
  color: #ff7034;
}

/* ── campo de data customizado ── */
.date-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 38px;
  padding: 0 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.15s;
  user-select: none;
}

.date-trigger:hover {
  border-color: #ff7034;
}

.date-trigger--active {
  border-color: #e0e0e0;
}

.date-trigger-text {
  font-size: 0.82rem;
  color: #444;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.date-arrow {
  color: #ff7034;
  font-size: 1rem;
  line-height: 1;
}

.date-trigger-icon {
  color: #ff7034;
  display: flex;
  align-items: center;
}

.date-trigger-icon ::v-deep .icon {
  margin: 0 !important;
}

/* ── opções do autocomplete ── */
.option-all {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  color: #ff5c1a;
  padding: 2px 0;
}

.option-all-icon {
  font-size: 1rem;
}

.option-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
}
.option-group-cnpj {
  color: hsl(0, 0%, 13%);
}
.option-group-company {
  font-size: 0.82rem;
  color: hsl(0, 0%, 45%);
  font-weight: 400;
}
.option-group-count {
  margin-left: auto;
  font-size: 0.75rem;
  color: hsl(0, 0%, 55%);
  font-weight: 400;
}
.option-store {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.option-cnpj-hint {
  font-size: 0.8rem;
  color: hsl(0, 0%, 55%);
}

/* ── responsividade ── */
@media (max-width: 600px) {
  .topbar-search {
    flex: 1 1 100%;
  }
  .topbar-actions {
    flex-shrink: 0;
  }
}
</style>
